import React, { useRef, useState } from 'react';
import useArrowNav from '../../hooks/useArrowNav';
import { Clock, Plus, MessageSquare, CheckSquare, Send, PhoneCall, ArrowRightLeft, Brain, Tag, FileText, FolderOpen, Settings, Zap, Mail, Activity, ChevronRight, Search, X } from 'lucide-react';
import { safeAsync, Badge, emptyMsg, btnPrimary, neuShadowSm, inp } from './crmShared';

export default function TimelineTab({ timelineContactId, setTimelineContactId, timeline, setTimeline, contacts, loadExtended, onContactClick }) {
  const activeContactRef = useRef(null);
  const timelineRef = useRef(null);
  const { onKeyDown: handleTimelineKeyDown } = useArrowNav(timelineRef, '[data-timeline-entry]', { horizontal: false, vertical: true });

  const [expandedTypes, setExpandedTypes] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const toggleEventType = (eventType) => {
    const newSet = new Set(expandedTypes);
    if (newSet.has(eventType)) newSet.delete(eventType);
    else newSet.add(eventType);
    setExpandedTypes(newSet);
  };

  const toggleAllEventTypes = () => {
    if (expandedTypes.size > 0) {
      setExpandedTypes(new Set());
    } else {
      const allTypes = [...new Set(timeline.map(e => e.event_type))];
      setExpandedTypes(new Set(allTypes));
    }
  };

  const typeConfig = {
    interaction: { color: '#2E8B98', icon: MessageSquare },
    task: { color: '#F26522', icon: CheckSquare },
    message: { color: '#62BB46', icon: Send },
    call: { color: '#F26522', icon: PhoneCall },
    stage_change: { color: '#256F7A', icon: ArrowRightLeft },
    score_change: { color: '#2E8B98', icon: Brain },
    tag_change: { color: '#06b6d4', icon: Tag },
    note: { color: '#6C757D', icon: FileText },
    document: { color: '#2E8B98', icon: FolderOpen },
    system: { color: '#ADB5BD', icon: Settings },
    referral: { color: '#d946ef', icon: ArrowRightLeft },
    workflow: { color: '#a78bfa', icon: Zap },
    email_campaign: { color: '#4FA038', icon: Mail },
  };

  // Group timeline by event_type
  const eventsByType = {};
  timeline.forEach(ev => {
    if (!eventsByType[ev.event_type]) eventsByType[ev.event_type] = [];
    eventsByType[ev.event_type].push(ev);
  });

  // Filter by search query
  const filteredEventsByType = {};
  Object.entries(eventsByType).forEach(([type, events]) => {
    const filtered = events.filter(ev =>
      (ev.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ev.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      type.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) {
      filteredEventsByType[type] = filtered;
    }
  });

  // Auto-expand on search
  if (searchQuery && expandedTypes.size === 0) {
    setExpandedTypes(new Set(Object.keys(filteredEventsByType)));
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={18} color="#2E8B98" /> Contact Timeline
        </h3>
      </div>

      {/* Contact selector */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <select value={timelineContactId || ''} onChange={async (e) => {
          const cid = e.target.value;
          setTimelineContactId(cid || null);
          if (cid) {
            activeContactRef.current = cid;
            const tl = await window.zignagenixAPI.getContactTimeline(parseInt(cid));
            if (activeContactRef.current !== cid) return;
            setTimeline(Array.isArray(tl) ? tl : []);
            setExpandedTypes(new Set());
            setSearchQuery('');
          }
          else setTimeline([]);
        }} style={{ ...inp, minWidth: 260 }}>
          <option value="">Select a contact to view timeline…</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.contact_type}</option>)}
        </select>
        {timelineContactId && (
          <button onClick={safeAsync(async () => {
            const note = prompt('Add a note:');
            if (!note) return;
            await window.zignagenixAPI.addTimelineNote({ contactId: parseInt(timelineContactId), title: 'Note', description: note });
            const tl = await window.zignagenixAPI.getContactTimeline(parseInt(timelineContactId)); setTimeline(Array.isArray(tl) ? tl : []);
          })} style={btnPrimary}><Plus size={13} /> Add Note</button>
        )}
      </div>

      {/* Timeline feed */}
      {!timelineContactId ? (
        <div style={{ background: '#F8F9FA', borderRadius: 14, padding: 40, textAlign: 'center', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)' }}>
          <div style={emptyMsg}>Select a contact above to view their complete activity timeline.</div>
        </div>
      ) : timeline.length === 0 ? (
        <div style={{ background: '#F8F9FA', borderRadius: 14, padding: 40, textAlign: 'center', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)' }}>
          <div style={emptyMsg}>No activity recorded for this contact yet.</div>
        </div>
      ) : (
        <div>
          {/* Search and controls */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, background: '#F8F9FA', padding: 12, borderRadius: 12, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)' }}>
            <Search size={16} color="#6C757D" />
            <input type="text" placeholder="Search by title, description, or type..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              style={{ ...inp, flex: 1, margin: 0 }} />
            {searchQuery && <button onClick={() => setSearchQuery('')} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#F8F9FA', color: '#6C757D', cursor: 'pointer', fontSize: 12, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4, boxShadow: neuShadowSm, transition: 'all 0.15s' }}><X size={14} /></button>}
            <button onClick={toggleAllEventTypes} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#F8F9FA', color: '#6C757D', cursor: 'pointer', fontSize: 12, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4, boxShadow: neuShadowSm, transition: 'all 0.15s' }}>
              {expandedTypes.size > 0 ? 'Collapse All' : 'Expand All'}
            </button>
          </div>

          {/* Timeline by type */}
          <div>
            {Object.entries(filteredEventsByType).map(([eventType, events]) => {
              const cfg = typeConfig[eventType] || { color: '#6C757D', icon: Activity };
              const TypeIcon = cfg.icon;
              const isOpen = expandedTypes.has(eventType);

              return (
                <div key={eventType} style={{ marginBottom: 16, background: '#F8F9FA', borderRadius: 14, overflow: 'hidden', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)' }}>
                  {/* Group header */}
                  <button
                    onClick={() => toggleEventType(eventType)}
                    style={{
                      width: '100%', padding: '14px 16px', border: 'none', background: '#F8F9FA',
                      display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                      fontSize: 13, fontWeight: 600, color: '#212529',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f0f2f7'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#F8F9FA'}
                  >
                    <ChevronRight
                      size={16}
                      color={cfg.color}
                      style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
                    />
                    <TypeIcon size={14} color={cfg.color} />
                    <span>{eventType.replace(/_/g, ' ')}</span>
                    <span style={{ fontSize: 11, background: cfg.color, color: '#fff', padding: '2px 8px', borderRadius: 10, marginLeft: 'auto' }}>{events.length}</span>
                  </button>

                  {/* Vertical timeline line and events */}
                  {isOpen && (
                    <div style={{
                      maxHeight: '2000px',
                      overflow: 'hidden',
                      transition: 'max-height 0.3s ease',
                      paddingLeft: 28,
                      paddingRight: 16,
                      paddingBottom: 16,
                      paddingTop: 0,
                      position: 'relative'
                    }}>
                      {/* Vertical line */}
                      <div style={{ position: 'absolute', left: 11, top: 0, bottom: 0, width: 2, background: cfg.color, borderRadius: 1, opacity: 0.3 }} />

                      <div ref={timelineRef} onKeyDown={handleTimelineKeyDown}>
                        {events.map((ev, i) => (
                          <div key={`${ev.event_type}-${ev.id}-${i}`} data-timeline-entry tabIndex={0} style={{ position: 'relative', marginBottom: 12, outline: 'none' }}
                            onFocus={(e) => { e.currentTarget.style.backgroundColor = '#e0e5ec'; e.currentTarget.style.borderRadius = '12px'; e.currentTarget.style.padding = '4px'; }}
                            onBlur={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.padding = '0'; }}>
                            {/* Dot */}
                            <div style={{ position: 'absolute', left: -22, top: 4, width: 14, height: 14, borderRadius: 7, background: cfg.color, border: '2px solid #F8F9FA', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <TypeIcon size={8} color="#fff" />
                            </div>
                            <div style={{ background: '#F8F9FA', borderRadius: 12, boxShadow: neuShadowSm, padding: '10px 14px', border: '1px solid #e0e5ec' }}>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 3, flexWrap: 'wrap' }}>
                                <Badge text={ev.event_type.replace(/_/g, ' ')} color={cfg.color} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{ev.title || ev.sub_type || '—'}</span>
                                {ev.status && <Badge text={ev.status} color={ev.status === 'completed' ? '#62BB46' : '#F26522'} />}
                                {ev.direction && <span style={{ fontSize: 10, color: '#ADB5BD' }}>{ev.direction === 'outbound' ? '→ out' : '← in'}</span>}
                                <span style={{ fontSize: 10, color: '#ADB5BD', marginLeft: 'auto' }}>{new Date(ev.created_at).toLocaleString()}</span>
                              </div>
                              {ev.description && <div style={{ fontSize: 11, color: '#6C757D' }}>{ev.description.substring(0, 200)}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {Object.keys(filteredEventsByType).length === 0 && searchQuery && (
              <div style={{ background: '#F8F9FA', borderRadius: 14, padding: 40, textAlign: 'center', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)' }}>
                <div style={{ color: '#ADB5BD', fontSize: 13 }}>No events matching your search.</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
